import type { IconComponent } from '@/components/icons/types';
import { Icon } from '@/components/ui/icon';
import { Text } from '@/components/ui/text';
import { cn } from '@/utils/helpers/cn';

import type { ReactNode } from 'react';

/**
 * Layout compartilhado de `EmptyState` e `ErrorState` — ícone, título, apoio e
 * ação, centralizados na área que sobrou.
 *
 * ## Por que os dois não são o mesmo componente com uma prop `variant`
 *
 * Porque a **decisão de qual usar é do consumidor e precisa ser legível na
 * chamada**. `<EmptyState>` e `<ErrorState>` dizem, no nome, qual dos dois
 * estados da tela está sendo desenhado; um `<StateBlock variant="error">`
 * empurraria essa leitura para dentro de uma string.
 *
 * ## E por que também não são dois arquivos independentes
 *
 * `docs/03` exige **API idêntica** entre eles, com o argumento de que são "o
 * mesmo gesto de layout" e que divergir obrigaria o desenvolvedor a lembrar
 * duas assinaturas para o mesmo problema. Duas cópias do JSX tornam essa
 * exigência uma promessa que a primeira mudança quebra. Com o layout aqui, a
 * identidade das duas APIs é **estrutural**: não há como uma ganhar um respiro
 * ou um tamanho de ícone que a outra não tenha.
 *
 * Este componente é interno ao par. Nenhuma tela deve importá-lo direto — quem
 * o faz está desenhando um terceiro estado sem nome, que é exatamente o que os
 * dois existem para evitar.
 */
export interface StateProps {
  /**
   * Substitui o ícone default da variante. É `ReactNode` e não `IconComponent`
   * porque o caso real de troca é uma peça de marca (o `SuccessSeal`, uma
   * ilustração), não outro glifo do registro.
   */
  icon?: ReactNode;
  title?: string;
  /**
   * Tag do título. Default `p`, porque o caso comum é o bloco ocupar **parte**
   * de uma tela que já tem seu `<h1>` no `PageHeader` — e ali um segundo
   * heading criaria um nível solto na árvore do documento.
   *
   * ⚠️ **Quando o bloco É a tela inteira** — a página "não encontrada", por
   * exemplo — passe `titleAs="h1"`. Sem isso a tela não tem cabeçalho nenhum, e
   * quem navega por headings (o atalho mais usado por leitor de tela para se
   * situar numa página nova) chega num documento mudo. Foi exatamente o que a
   * primeira versão do 404 produziu.
   */
  titleAs?: 'p' | 'h1' | 'h2';
  description?: string;
  /** Normalmente um `<Button>`. No `ErrorState`, quase sempre "Tentar de novo". */
  action?: ReactNode;
  className?: string;
}

export function StateBlock({
  defaultIcon,
  icon,
  title,
  titleAs = 'p',
  description,
  action,
  className,
}: StateProps & { defaultIcon: IconComponent }) {
  return (
    <div
      className={cn(
        // `py-10` e não uma altura fixa: o bloco ocupa o que a tela der. Altura
        // fixa deixaria uma faixa vazia embaixo em tela curta e um bloco
        // perdido no meio do branco em tela longa.
        'flex flex-col items-center gap-3 px-6 py-10 text-center',
        className,
      )}
    >
      {/*
        O ícone é decorativo: o título ao lado diz a mesma coisa em palavras, e
        anunciá-lo faria o leitor de tela repetir o estado duas vezes. Quem
        passa um `icon` próprio é responsável pelo `aria` dele.
      */}
      {icon ?? <Icon as={defaultIcon} size="xl" className="text-muted-foreground" />}

      <div className="flex flex-col gap-1">
        {title ? (
          <Text variant="title" as={titleAs}>
            {title}
          </Text>
        ) : null}

        {description ? (
          <Text variant="body" tone="muted">
            {description}
          </Text>
        ) : null}
      </div>

      {/* `pt-1` só quando há ação: sem ele o botão encosta no texto; com ele
          sempre, sobraria um respiro solto no bloco sem ação. */}
      {action ? <div className="flex w-full max-w-xs flex-col pt-1">{action}</div> : null}
    </div>
  );
}
